import React, { useEffect, useState } from 'react';
import { useGamingStore, Game } from '../../store/gamingStore';
import { Star } from 'lucide-react';
import { extractDominantColor } from '../../lib/colorExtract';

interface GameCardProps {
  game: Game;
  owned?: boolean;
}

const GameCard: React.FC<GameCardProps> = ({ game, owned }) => {
  const { openGameDetail } = useGamingStore();
  const [accentColor, setAccentColor] = useState<string | null>(null);

  useEffect(() => {
    if (game.cover_image_url) {
      extractDominantColor(game.cover_image_url).then(setAccentColor);
    }
  }, [game.cover_image_url]);

  const formatPrice = (price: number, salePrice: number | null) => {
    const p = Number(price);
    if (p === 0) return 'Gratuit';
    if (salePrice !== null && salePrice !== undefined) {
      return (
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-text-muted line-through">{p.toFixed(2)}$</span>
          <span className="text-gaming-accent font-bold">{Number(salePrice).toFixed(2)}$</span>
        </div>
      );
    }
    return <span className="font-bold">{p.toFixed(2)}$</span>;
  };

  return (
    <div
      className="group relative flex flex-col rounded-lg overflow-hidden bg-bg-secondary border border-border-color transition-all duration-300 cursor-pointer hover:shadow-lg hover:-translate-y-0.5"
      onClick={() => openGameDetail(game.slug)}
      onMouseEnter={(e) => {
        if (accentColor) {
          e.currentTarget.style.borderColor = accentColor.replace('rgb', 'rgba').replace(')', ', 0.45)');
          e.currentTarget.style.boxShadow = `0 8px 24px ${accentColor.replace('rgb', 'rgba').replace(')', ', 0.15)')}`;
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = '';
        e.currentTarget.style.boxShadow = '';
      }}
    >
      {/* Cover Image */}
      <div className="relative aspect-[2/3] overflow-hidden bg-bg-tertiary">
        {game.cover_image_url ? (
          <img
            src={game.cover_image_url}
            alt={game.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gaming-accent/20 to-bg-tertiary">
            <span className="text-2xl font-black text-gaming-accent/40">{game.title.charAt(0)}</span>
          </div>
        )}

        {/* Sale badge */}
        {game.sale_price !== null && game.sale_price !== undefined && Number(game.price) > 0 && (
          <div className="absolute top-2 left-2 bg-gaming-accent text-black text-[10px] font-bold px-1.5 py-0.5 rounded">
            -{Math.round(((Number(game.price) - Number(game.sale_price)) / Number(game.price)) * 100)}%
          </div>
        )}

        {/* Owned badge */}
        {owned && (
          <div className="absolute top-2 right-2 bg-success/90 text-black text-[10px] font-bold px-1.5 py-0.5 rounded">
            Possédé
          </div>
        )}

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
          <div className="flex items-center gap-2 text-xs text-white/80">
            {Number(game.rating_avg) > 0 && (
              <span className="flex items-center gap-0.5">
                <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />
                {Number(game.rating_avg).toFixed(1)}
              </span>
            )}
            {game.genre && <span className="bg-white/20 px-1.5 py-0.5 rounded text-[10px]">{game.genre}</span>}
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="p-2.5 flex-1 flex flex-col">
        <h3 className="text-sm font-semibold text-text-primary truncate group-hover:text-gaming-accent transition-colors">
          {game.title}
        </h3>
        <p className="text-[11px] text-text-muted mt-0.5 truncate">{game.developer}</p>
        <div className="mt-auto pt-2 text-xs text-text-secondary">
          {formatPrice(game.price, game.sale_price)}
        </div>
      </div>
    </div>
  );
};

export default GameCard;
